// Cache merge utilities to prevent accidental chapter data loss
export interface Chapter {
  id: string;
  title?: string;
  previewContent?: string;
  previewSummary?: string;
  previewWordCountTarget?: number;
  status?: string;
  contentCache?: {
    hasPreview?: boolean;
    previewWordCount?: number;
    hasFullText?: boolean;
    fullTextWordCount?: number;
    [key: string]: any;
  };
  progress?: {
    hasPreviewText?: boolean;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Safely merge chapters by ID, preventing data loss
 * Always preserves existing data when merging
 */
export function safeMergeChapters(
  prev: Chapter[] | undefined,
  next: Chapter | Chapter[]
): Chapter[] {
  const base = Array.isArray(prev) ? prev.slice() : [];
  const incoming = Array.isArray(next) ? next : [next];

  const byId = new Map(base.map(c => [c.id, c]));

  for (const ch of incoming) {
    const existing = byId.get(ch.id);
    byId.set(ch.id, {
      ...(existing ?? {}),
      ...ch,
      // Deep merge contentCache and progress
      contentCache: {
        ...(existing?.contentCache ?? {}),
        ...(ch.contentCache ?? {})
      },
      progress: {
        ...(existing?.progress ?? {}),
        ...(ch.progress ?? {})
      }
    });
  }

  return Array.from(byId.values());
}

/**
 * Check if a chapter has any generated content (preview or full text)
 */
function hasGeneratedContent(chapter: Chapter): boolean {
  const hasPreview = chapter.previewContent && chapter.previewContent.length > 0;
  const hasFullText = chapter.contentCache?.hasFullText === true ||
    (chapter.contentCache?.fullTextWordCount && chapter.contentCache.fullTextWordCount > 0);
  return !!(hasPreview || hasFullText);
}

/**
 * Check if chapters array has any chapters with generated content
 */
function hasChaptersWithContent(chapters: Chapter[]): boolean {
  return chapters.some(ch => hasGeneratedContent(ch));
}

/**
 * Prevents overwriting non-empty arrays with empty arrays AND protects generated content
 * This is the key protection against cache race conditions and stale data overwrites
 */
export function guardNonEmptyWrite(
  prev: Chapter[] | undefined,
  candidate: Chapter[] | undefined
): Chapter[] {
  const p = prev ?? [];
  const c = candidate ?? [];

  // If previous had data and candidate is empty, keep previous
  if (c.length === 0 && p.length > 0) {
    console.log('🛡️ guardNonEmptyWrite: Prevented empty array overwrite, keeping', p.length, 'chapters');
    return p;
  }

  // ENHANCED PROTECTION: If previous chapters have generated content and candidate doesn't, prefer previous
  if (p.length > 0 && c.length > 0) {
    const prevHasContent = hasChaptersWithContent(p);
    const candidateHasContent = hasChaptersWithContent(c);

    if (prevHasContent && !candidateHasContent) {
      console.log('🛡️ guardNonEmptyWrite: Prevented stale data overwrite - keeping chapters with generated content');
      console.log('   Previous chapters with content:', p.filter(ch => hasGeneratedContent(ch)).length);
      console.log('   Candidate chapters with content:', c.filter(ch => hasGeneratedContent(ch)).length);
      return p;
    }

    // If candidate has generated content but previous doesn't, prefer candidate
    if (candidateHasContent && !prevHasContent) {
      console.log('✅ guardNonEmptyWrite: Accepting fresher data with generated content');
      return c;
    }

    // If both have generated content, do intelligent comparison
    if (prevHasContent && candidateHasContent) {
      const prevContentCount = p.filter(ch => hasGeneratedContent(ch)).length;
      const candidateContentCount = c.filter(ch => hasGeneratedContent(ch)).length;

      if (prevContentCount >= candidateContentCount) {
        console.log('🛡️ guardNonEmptyWrite: Previous data has equal/more generated content, keeping previous');
        return p;
      } else {
        console.log('✅ guardNonEmptyWrite: Candidate has more generated content, accepting candidate');
        return c;
      }
    }
  }

  return c;
}

/**
 * Client write epoch tracking to prevent stale server overwrites
 */
let clientWriteEpoch = 0;

export function bumpClientEpoch(): number {
  clientWriteEpoch++;
  console.log('📈 Client write epoch bumped to', clientWriteEpoch);
  return clientWriteEpoch;
}

export function getCurrentClientEpoch(): number {
  return clientWriteEpoch;
}

/**
 * Check if server response is stale compared to client writes
 */
export function isServerResponseStale(
  cacheData: any,
  serverEpoch: number | undefined
): boolean {
  const cacheEpoch = cacheData?.__clientWriteEpoch ?? 0;
  const reqEpoch = serverEpoch ?? 0;

  const isStale = cacheEpoch > reqEpoch;
  if (isStale) {
    console.log('⏰ Server response is stale:', { cacheEpoch, reqEpoch });
  }

  return isStale;
}